import { useId, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface WeightTicketEntryCardProps {
  title: string;
  hint: string;
  timeLabel: string;
  vehicleLabel: string;
  grossLabel: string;
  tareLabel: string;
  netLabel: string;
  blankValueLabel: string;
  timeField: ReactNode;
  vehicleField: ReactNode;
  netField: ReactNode;
  className?: string;
}

const StaticWeightCell = ({
  label,
  blankValueLabel,
  testId,
}: {
  label: string;
  blankValueLabel: string;
  testId: string;
}) => (
  <div
    className="grid min-h-14 grid-cols-[5.5rem_minmax(0,1fr)] border-t border-efs-border first:border-t-0"
    data-testid={testId}
    data-ticket-row={testId.replace('weight-ticket-', '').replace('-static', '')}
  >
    <div className="flex items-center border-r border-efs-border bg-efs-bg-subtle px-3 text-sm font-semibold">
      {label}
    </div>
    <div className="min-h-14 bg-background px-3 py-2" aria-label={blankValueLabel}>
      <span className="sr-only">{blankValueLabel}</span>
    </div>
  </div>
);

const EditableWeightCell = ({
  label,
  children,
  row,
}: {
  label: string;
  children: ReactNode;
  row: 'time' | 'vehicle' | 'net';
}) => (
  <div
    className="grid min-h-14 grid-cols-[5.5rem_minmax(0,1fr)] border-t border-efs-border first:border-t-0"
    data-testid={`weight-ticket-${row}-row`}
    data-ticket-row={row}
  >
    <div className="flex items-center border-r border-efs-border bg-efs-bg-subtle px-3 text-sm font-semibold">
      {label}
    </div>
    <div className="min-w-0 bg-background px-3 py-2">{children}</div>
  </div>
);

export const WeightTicketEntryCard = ({
  title,
  hint,
  timeLabel,
  vehicleLabel,
  grossLabel,
  tareLabel,
  netLabel,
  blankValueLabel,
  timeField,
  vehicleField,
  netField,
  className,
}: WeightTicketEntryCardProps) => {
  const titleId = useId();

  return (
    <section
      aria-labelledby={titleId}
      className={cn('rounded-md border border-efs-border bg-card p-3 sm:p-4', className)}
    >
      <div className="mb-3 text-center">
        <h3 id={titleId} className="text-base font-semibold tracking-[0.18em] text-foreground">
          {title}
        </h3>
        <p className="mt-1 text-xs tracking-normal text-muted-foreground">{hint}</p>
      </div>
      <div className="overflow-hidden rounded-sm border-2 border-efs-border">
        <EditableWeightCell label={timeLabel} row="time">
          {timeField}
        </EditableWeightCell>
        <EditableWeightCell label={vehicleLabel} row="vehicle">
          {vehicleField}
        </EditableWeightCell>
        <StaticWeightCell
          label={grossLabel}
          blankValueLabel={blankValueLabel}
          testId="weight-ticket-gross-static"
        />
        <StaticWeightCell
          label={tareLabel}
          blankValueLabel={blankValueLabel}
          testId="weight-ticket-tare-static"
        />
        <EditableWeightCell label={netLabel} row="net">
          {netField}
        </EditableWeightCell>
      </div>
    </section>
  );
};
